import {
  GSPX_TRACKING_IDS,
  PRODUCTION_HOSTNAMES,
} from './constants/internalConstants';

/**
 * Determines the GainSightPX tracking ID based on the current hostname.
 * Returns prod ID for production hostnames and hostnames containing 'prod',
 * staging ID for hostnames containing 'staging' or 'sandbox',
 * qa ID for hostnames containing 'qa',
 * and falls back to dev ID for all other cases.
 */
export const determineGSPXTrackingID = (): string => {
  const { hostname } = window.location;

  if (
    PRODUCTION_HOSTNAMES.some(host => host === hostname) ||
    hostname.includes('prod')
  )
    return GSPX_TRACKING_IDS.prod;
  if (hostname.includes('staging') || hostname.includes('sandbox'))
    return GSPX_TRACKING_IDS.staging;
  if (hostname.includes('qa')) return GSPX_TRACKING_IDS.qa;

  return GSPX_TRACKING_IDS.dev;
};

interface GlobalWithProcessEnv {
  process?: {
    env?: Record<string, string | undefined>;
  };
}

export const getGlobalEnvVar = (key: string): string | undefined => {
  const globalWithEnv = globalThis as unknown as GlobalWithProcessEnv;
  return globalWithEnv.process?.env?.[key];
};

/**
 * Safely parses a date string to a Unix timestamp.
 */
export const safeParseDate = (dateString?: string | null): number | null => {
  if (!dateString) return null;
  const parsed = Date.parse(dateString);
  return isNaN(parsed) ? null : parsed;
};

/**
 * Gets the environment suffix based on the tracking ID.
 * Used for differentiating user IDs across different environments.
 */
export const getEnvironmentSuffix = (trackingID: string): string => {
  switch (trackingID) {
    case GSPX_TRACKING_IDS.staging:
      return 'staging';
    case GSPX_TRACKING_IDS.qa:
      return 'qa';
    case GSPX_TRACKING_IDS.dev:
      return 'dev';
    default:
      return 'prod';
  }
};

/**
 * Helper function to ensure undefined values are preserved in nested objects
 */
export const preserveUndefined = (obj: any): any => {
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        key,
        value === undefined ? null : preserveUndefined(value),
      ]),
    );
  }
  return obj;
};

/**
 * Default error capture function that logs to console
 */
export const defaultCaptureError = (error: Error, scope: any = {}): void => {
  if (getGlobalEnvVar('NODE_ENV') === 'development') {
    // eslint-disable-next-line no-console
    console.error('[GainSight Error]', error.message, {
      error,
      scope,
      stack: error.stack,
    });
  }
};
